import { useEffect, useState } from 'react';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';

interface UseTeamPathsResult {
  teamPaths: string[];
  loading: boolean;
  error?: Error;
}

/**
 * Hook to search for team paths in the Backstage catalog
 * Search for groups with the annotation 'gitlab.com/team-path'
 */
export const useTeamPaths = (): UseTeamPathsResult => {
  const catalogApi = useApi(catalogApiRef);
  const [teamPaths, setTeamPaths] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | undefined>(undefined);

  useEffect(() => {
    const fetchTeamPaths = async () => {
      try {
        setLoading(true);
        setError(undefined);

        // Search for all entities of type 'group'
        const { items } = await catalogApi.getEntities({
          filter: {
            kind: 'group',
          },
          fields: ['metadata.annotations'],
        });

        // Filter only groups that have the annotation and extract the values
        const paths = items
          .filter(
            entity => entity.metadata.annotations?.['gitlab.com/team-path'],
          )
          .map(entity => entity.metadata.annotations?.['gitlab.com/team-path'])
          .filter((path): path is string => Boolean(path))
          .filter((path, index, self) => self.indexOf(path) === index) // remove duplicates
          .sort();

        setTeamPaths(paths);
      } catch (err) {
        setError(err as Error);
        setTeamPaths([]);
      } finally {
        setLoading(false);
      }
    };

    fetchTeamPaths();
  }, [catalogApi]);

  return {
    teamPaths,
    loading,
    error,
  };
};
